import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { EnvironmentService } from '@pinnakl/core/environment';
import { AllowedUserTypeAuthParam, AppNames, UserTypes } from '@pinnakl/shared/types';
import { PinnaklUIToastMessage } from '@pinnakl/shared/util-providers';
import { firstValueFrom } from 'rxjs';
import { AuthCacheService } from './auth-cache.service';

@Injectable({
  providedIn: 'root',
})
export class AuthPagesHelpersService {
  private readonly http = inject(HttpClient);
  private readonly toast = inject(PinnaklUIToastMessage);
  private readonly authCacheService = inject(AuthCacheService);
  private readonly environmentService = inject(EnvironmentService);
  private readonly baseUrl = this.environmentService.get('authUrl');
  private readonly version = this.environmentService.version;

  get authUrls(): Record<
    'login' | 'linkLogin' | 'generateLink' | 'verifyotp' | 'verifyQrOtp' | 'clients' | 'selectClient',
    string
  > {
    return {
      login: `${this.baseUrl}/account/login`,
      clients: `${this.baseUrl}/account/clients`,
      selectClient: `${this.baseUrl}/account/select-client`,
      linkLogin: `${this.baseUrl}/account/link/login`,
      generateLink: `${this.baseUrl}/account/link`,
      verifyotp: `${this.baseUrl}/account/verifyotp`,
      verifyQrOtp: `${this.baseUrl}/account/totp/verify`,
    };
  }

  async selectClientLinkNavigated(): Promise<{ id: number; name: string }[] | null> {
    this.checkAppVersioning();
    return await this.getClientsToSelect();
  }

  // The server sends cookies in Set-Cookie header. Without this, Angular will ignore the Set-Cookie header
  async login(login: { username: string; password: string }): Promise<void> {
    const user = {
      ...login,
      application: 'Desktop',
      userType: this.getUserType(),
    };
    const loginUrl = `${this.baseUrl}/account/login` + this.authCacheService.lastURLSearch;
    const res = await firstValueFrom(this.http.post<{ returnUrl: string }>(loginUrl, user, { withCredentials: true }));
    if (res && res.returnUrl?.includes('/2fa')) {
      this.authCacheService.credentials = login;
    }
    this.redirectToSource(res?.returnUrl);
  }

  redirectToSource(redirectUri?: string): void {
    window.location.assign(redirectUri ?? this.authCacheService.sourceAppURL);
  }

  async selectClient(clientId: number): Promise<{ returnUrl: string }> {
    const search = new URLSearchParams(this.authCacheService.lastURLSearch);
    const returnUrl = search.get('returnUrl');

    if (!returnUrl) {
      throw new Error('Return URL is not found!');
    }
    return await firstValueFrom(
      this.http.post<{ returnUrl: string }>(
        `${this.authUrls.selectClient}?returnUrl=${encodeURIComponent(returnUrl)}`,
        {
          clientId,
        },
        { withCredentials: true },
      ),
    );
  }

  async magicLinkNavigated(): Promise<void> {
    this.checkAppVersioning();

    if (!this.authCacheService.lastURLSearch) {
      this.toast.error('Invalid URL! No needed params.');
    }

    const search = new URLSearchParams(this.authCacheService.lastURLSearch);
    const token = search.get('token');

    if (!token) {
      throw new Error('Token is not found!');
    }

    const res = await this.postAccountLinkLogin(token);

    const returnUrl = search.get('returnUrl');
    const otpChannel = search.get('otpChannel');
    this.redirectToSource(otpChannel === 'qr' || otpChannel === 'mobile' ? res.returnUrl : returnUrl);
  }

  async regenerateMagicLink(email: string): Promise<void> {
    await firstValueFrom(
      this.http.post<void>(
        this.authUrls.generateLink,
        {
          email,
          usertype: this.getUserType(),
          returnUrl: this.authCacheService.sourceAppURL,
        },
        { withCredentials: true },
      ),
    );
  }

  async verifyQrOtp(otp: string, isQr = false): Promise<void> {
    const params = new URLSearchParams(this.authCacheService.lastURLSearch);
    const token = params.get('token');
    const returnUrlPath = encodeURIComponent(params.get('returnUrl') ?? '');
    const verifyUrl = `${isQr ? this.authUrls.verifyQrOtp : this.authUrls.verifyotp}?token=${token}&otp=${otp}&returnUrl=${returnUrlPath}`;

    const { returnUrl } = await firstValueFrom(
      this.http.get<{ returnUrl: string }>(verifyUrl, { withCredentials: true }),
    );
    this.authCacheService.clearCredentials();
    this.redirectToSource(returnUrl);
  }

  public getUserType(): UserTypes {
    return this.authCacheService.allowedUserType === AllowedUserTypeAuthParam.EXTERNAL
      ? UserTypes.EXTERNAL
      : UserTypes.INTERNAL;
  }

  private async postAccountLinkLogin(token: string): Promise<any> {
    const isPortalApp =
      this.environmentService.get<AppNames>('appName') === AppNames.INVESTOR_PORTAL ||
      this.environmentService.get<AppNames>('appName') === AppNames.DEDICATED_INVESTOR_PORTAL;
    // For the portal app we need to build URL with redirect_uri otherwise BE do redirection to separate auth app
    const url = isPortalApp
      ? `${this.authUrls.linkLogin}?returnUrl=${encodeURIComponent(this.authCacheService.sourceAppURL)}`
      : this.authUrls.linkLogin;
    return firstValueFrom(this.http.post(url, { token }, { withCredentials: true }));
  }

  private checkAppVersioning(): void {
    const isLatestAppVersion = this.isLatestAppVersion();

    if (!isLatestAppVersion) {
      this.setCurrentAppVersion();
      window.location.assign(window.location.href);
      throw new Error('App version is not latest!');
    }
  }

  private async getClientsToSelect(): Promise<{ id: number; name: string }[]> {
    const url = this.authUrls.clients;
    return firstValueFrom(this.http.get<{ id: number; name: string }[]>(url, { withCredentials: true }));
  }

  private isLatestAppVersion(): boolean {
    const appVersion = localStorage.getItem('appVersion');
    if (!appVersion) return false;
    return this.version === appVersion;
  }

  private setCurrentAppVersion(): void {
    localStorage.setItem('appVersion', this.version);
  }
}
